import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Pencil, RotateCcw, Copy, Eye, EyeOff, GripVertical, Trash2, Plus, ExternalLink, Download, Mail, Lock, Unlock, Ban, UserPlus, CheckCircle2, CircleDashed, Gift, Users as UsersIcon, QrCode } from "lucide-react";
import { formatEventDate } from "@/lib/utils";
import { Link } from "react-router-dom";
import ComposeEmailDialog from "@/components/ComposeEmailDialog";
import ConfirmationViewerDialog from "@/components/ConfirmationViewerDialog";
import AddAttendeeDialog from "@/components/AddAttendeeDialog";

const PAYMENT_METHOD_LABELS = {
  card: "Credit / Debit card",
  link: "Link (Stripe one-click)",
  klarna: "Klarna (Pay in 4 / Pay later)",
  afterpay_clearpay: "Afterpay / Clearpay",
};
const PAYMENT_METHOD_DESCRIPTIONS = {
  card: "Visa, Mastercard, Amex, Discover — always on. Apple Pay + Google Pay ride on this.",
  link: "Stripe's saved-card wallet. Returning shoppers check out in one click.",
  klarna: "Buy-now-pay-later. Stripe hides it under ~$10 or outside eligible regions.",
  afterpay_clearpay: "4 interest-free payments. Stripe hides it under ~$1 or outside eligible regions.",
};
const SECTION_LABELS = {
  "home.sections.upcoming.visible":     { title: "Upcoming events",   description: "The 'On the calendar' grid showing your next 3 events near the top." },
  "home.sections.experiences.visible":  { title: "What we do",        description: "The 4-card lineup (Themed, Social, Couples, Singles)." },
  "home.sections.story.visible":        { title: "The experience",    description: "The 'Night is the plan' brand story with your stat cards." },
  "home.sections.connect.visible":      { title: "Stay in the loop",  description: "The 'Join the list' card with social links + account/schedule CTAs." },
  "home.sections.manifesto.visible":    { title: "Manifesto",         description: "The dark 'Where the Wi-Fi ends' promise block near the footer." },
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState({});
  const [setupStatus, setSetupStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [selEvent, setSelEvent] = useState(null);
  const [form, setForm] = useState({
    title:"", description:"", venue:"", address:"", date:"", time:"7:30 PM",
    age_min: 25, age_max: 45, price_cents: 5000, capacity: 30,
    image_url: "https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1600&q=80",
    image_focal_x: 50, image_focal_y: 50,
    parking_info: "", dress_code: "Smart casual", category: "Singles",
    schedule: [], faq: [], is_published: true,
    registration_open: true, force_full: false, allow_waitlist_when_full: true,
  });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create, id = edit
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef();
  const [cms, setCms] = useState([]);
  const [cmsSaving, setCmsSaving] = useState(null);

  const [recent, setRecent] = useState([]);
  const [emailEventOpen, setEmailEventOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [confViewerTicketId, setConfViewerTicketId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState({ methods: [], available: [] });
  const [pmSaving, setPmSaving] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [addAttendeeOpen, setAddAttendeeOpen] = useState(false);
  const [bulkGiftOpen, setBulkGiftOpen] = useState(false);

  const load = async () => {
    const [a, e, u] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/events/all"),
      api.get(`/admin/users${showDeleted ? "?include_deleted=true" : ""}`),
    ]);
    setAnalytics(a.data); setEvents(e.data); setUsers(u.data);
    api.get("/admin/setup-status").then(r => setSetupStatus(r.data)).catch(err => console.warn("setup-status fetch failed", err));
    api.get("/admin/cms/content").then(r => setCms(r.data)).catch(err => console.warn("cms fetch failed", err));
    api.get("/admin/sales/recent?limit=20").then(r => setRecent(r.data)).catch(err => console.warn("recent purchases fetch failed", err));
    api.get("/admin/payment-settings").then(r => setPaymentMethods(r.data)).catch(err => console.warn("payment settings fetch failed", err));
  };
  useEffect(() => { load(); }, [showDeleted]);  // eslint-disable-line react-hooks/exhaustive-deps

  const emptyForm = {
    title:"", description:"", venue:"", address:"", date:"", time:"7:30 PM",
    age_min: 25, age_max: 45, price_cents: 5000, capacity: 30,
    image_url: "https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1600&q=80",
    image_focal_x: 50, image_focal_y: 50,
    parking_info: "", dress_code: "Smart casual", category: "Singles",
    schedule: [], faq: [], is_published: true,
    registration_open: true, force_full: false, allow_waitlist_when_full: true,
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (ev) => {
    setEditingId(ev.event_id);
    setForm({ ...emptyForm, ...ev, schedule: ev.schedule || [], faq: ev.faq || [],
      image_focal_x: ev.image_focal_x ?? 50, image_focal_y: ev.image_focal_y ?? 50,
      is_published: ev.is_published !== false,
      registration_open: ev.registration_open !== false,
      force_full: !!ev.force_full,
      allow_waitlist_when_full: ev.allow_waitlist_when_full !== false });
    setOpen(true);
  };

  const saveEvent = async () => {
    // Strip synthetic _key added by ItemListEditor before sending to backend
    const stripKey = (arr) => (arr || []).map(({ _key, ...rest }) => rest);
    const payload = { ...form,
      price_cents: +form.price_cents, capacity: +form.capacity,
      age_min: +form.age_min, age_max: +form.age_max,
      image_focal_x: +form.image_focal_x, image_focal_y: +form.image_focal_y,
      schedule: stripKey(form.schedule), faq: stripKey(form.faq),
      is_published: !!form.is_published,
      registration_open: !!form.registration_open,
      force_full: !!form.force_full,
      allow_waitlist_when_full: !!form.allow_waitlist_when_full,
    };
    try {
      if (editingId) {
        await api.patch(`/admin/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const uploadEventPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingId) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5 MB");
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/events/${editingId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.image_url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const saveCms = async (key, value) => {
    setCmsSaving(key);
    try {
      await api.put(`/admin/cms/content/${encodeURIComponent(key)}`, { value });
      toast.success("Saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setCmsSaving(null); }
  };

  const resetCms = async (key) => {
    if (!window.confirm("Reset this content to default?")) return;
    try {
      await api.delete(`/admin/cms/content/${encodeURIComponent(key)}`);
      toast.success("Reset to default"); load();
    } catch (err) { toast.error("Reset failed"); }
  };

  const uploadCmsImage = async (key, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    setCmsSaving(key);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/cms/content/${encodeURIComponent(key)}`, { value: data.url });
      toast.success("Image updated"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setCmsSaving(null); }
  };

  const deleteEvent = async (id, hasPaid) => {
    let force = false;
    if (hasPaid) {
      const ok = window.confirm("This event has PAID TICKETS. Choose:\n\nOK = Auto-refund all attendees + delete the event\nCancel = do nothing");
      if (!ok) return;
      force = true;
    } else {
      if (!window.confirm("Delete this event permanently? This cannot be undone.")) return;
    }
    try {
      const { data } = await api.delete(`/admin/events/${id}${force ? "?force=true" : ""}`);
      if (data.refunded_count > 0) toast.success(`Refunded ${data.refunded_count} ticket(s) and deleted event`);
      else toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const duplicateEvent = async (id) => {
    try {
      await api.post(`/admin/events/${id}/duplicate`);
      toast.success("Event duplicated (as draft)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duplicate failed");
    }
  };

  const togglePaymentMethod = async (method) => {
    const current = paymentMethods.methods || [];
    const isOn = current.includes(method);
    // Card is always required — protect against accidental removal.
    if (method === "card") return toast.info("Card is always enabled — it's the fallback payment method.");
    const next = isOn ? current.filter(m => m !== method) : [...current, method];
    setPmSaving(true);
    try {
      const { data } = await api.put("/admin/payment-settings", { methods: next });
      setPaymentMethods(data);
      toast.success(isOn ? `${PAYMENT_METHOD_LABELS[method] || method} disabled` : `${PAYMENT_METHOD_LABELS[method] || method} enabled`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setPmSaving(false);
    }
  };

  const togglePublish = async (id, current) => {
    try {
      await api.patch(`/admin/events/${id}/publish?is_published=${!current}`);
      toast.success(!current ? "Published" : "Moved to draft");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const patchRegistration = async (id, updates, successMsg) => {
    try {
      await api.patch(`/admin/events/${id}/registration`, updates);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const promptCapacity = async (ev) => {
    const raw = window.prompt(`Set new capacity for "${ev.title}"\nCurrently: ${ev.capacity} · Sold: ${ev.tickets_sold}`, String(ev.capacity ?? 0));
    if (raw === null) return;
    const val = parseInt(raw, 10);
    if (!Number.isFinite(val) || val < 0) return toast.error("Enter a whole number ≥ 0");
    patchRegistration(ev.event_id, { capacity: val }, `Capacity updated to ${val}`);
  };

  const updateUser = async (userId, updates) => {
    try {
      await api.patch(`/admin/users/${userId}`, updates);
      toast.success("User updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const suspendUser = async (userId, currentlySuspended) => {
    if (currentlySuspended) {
      if (!window.confirm("Reactivate this user's account?")) return;
      try { await api.post(`/admin/users/${userId}/unsuspend`); toast.success("User reactivated"); load(); }
      catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
      return;
    }
    const reason = window.prompt("Suspension reason (shown to the user on login):", "");
    if (reason === null) return;
    try { await api.post(`/admin/users/${userId}/suspend`, { reason }); toast.success("User suspended"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const grantVip = async (userId, currentlyVip) => {
    const label = currentlyVip ? "Revoke VIP status?" : "Grant complimentary VIP status?";
    if (!window.confirm(label)) return;
    try { await api.patch(`/admin/users/${userId}`, { is_vip: !currentlyVip }); toast.success(currentlyVip ? "VIP revoked" : "VIP granted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const sendResetForUser = async (userId, email) => {
    if (!window.confirm(`Send a password reset email to ${email}?`)) return;
    try { await api.post(`/admin/users/${userId}/send-reset`); toast.success(`Reset link emailed to ${email}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const viewAttendees = async (id) => {
    const { data } = await api.get(`/admin/events/${id}/attendees`);
    setAttendees(data); setSelEvent(id);
  };

  const viewWaitlist = async (id) => {
    try { const { data } = await api.get(`/admin/events/${id}/waitlist`); setWaitlist(data); setSelEvent(id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load waitlist"); }
  };

  const refund = async (ticketId) => {
    if (!window.confirm("Issue a full refund?")) return;
    try { await api.post(`/admin/refund/${ticketId}`); toast.success("Refunded"); viewAttendees(selEvent); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  const toggleCheckIn = async (ticket) => {
    try {
      if (ticket.checked_in_at) {
        await api.post(`/admin/tickets/${ticket.ticket_id}/undo-check-in`);
        toast.success("Check-in reversed");
      } else {
        await api.post(`/admin/tickets/${ticket.ticket_id}/check-in`);
        toast.success(`${ticket.user?.name || "Attendee"} checked in`);
      }
      viewAttendees(selEvent);
    } catch (e) { toast.error(e?.response?.data?.detail || "Check-in failed"); }
  };

  const exportAttendeesCsv = () => {
    if (!selEvent) return;
    const token = localStorage.getItem("oa_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${selEvent}/attendees/export.csv`;
    // Use fetch so we can send the auth header, then trigger download client-side
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("export failed"); return r.blob(); })
      .then(blob => {
        const a = document.createElement("a");
        const href = URL.createObjectURL(blob);
        a.href = href; a.download = `attendees_${selEvent}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(href);
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  const resendAllConfirmations = async () => {
    if (!selEvent) return;
    const paidCount = attendees.filter(a => a.status === "paid").length;
    if (paidCount === 0) return toast.error("No paid attendees to resend to");
    if (!window.confirm(`Resend the branded confirmation email (with scannable QR) to all ${paidCount} paid attendee${paidCount === 1 ? "" : "s"} of this event?\n\nUse this to backfill QR codes to anyone who bought before QR support was added.`)) return;
    try {
      const { data } = await api.post(`/admin/events/${selEvent}/resend-confirmations`, {});
      toast.success(`Resending to ${data.count} attendees in the background…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const removeWaitlistEntry = async (entry) => {
    if (!window.confirm(`Remove ${entry.user?.name || entry.user?.email || "this entry"} from the waitlist?`)) return;
    try { await api.delete(`/admin/waitlist/${entry.waitlist_id}`); toast.success("Removed from waitlist"); viewWaitlist(selEvent); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const promoteWaitlistEntry = async (entry) => {
    if (!window.confirm(`Promote ${entry.user?.name || entry.user?.email} to a comp ticket? They'll be emailed a confirmation.`)) return;
    try { await api.post(`/admin/waitlist/${entry.waitlist_id}/promote`); toast.success("Promoted to attendee"); viewWaitlist(selEvent); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Promote failed"); }
  };

  const deleteUser = async (userRow) => {
    if (!window.confirm(`Delete ${userRow.name || userRow.email}? Their tickets stay on file for your records, but they'll be removed from the admin list and can't log back in.`)) return;
    try { await api.delete(`/admin/users/${userRow.user_id}`); toast.success(`${userRow.email} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const restoreUser = async (userRow) => {
    try { await api.post(`/admin/users/${userRow.user_id}/restore`); toast.success(`${userRow.email} restored`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-dashboard">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-eyebrow">Admin</div>
          <h1 className="font-heading text-4xl md:text-5xl mt-2">Command center</h1>
        </div>
        {setupStatus?.modes?.stripe && (
          <div
            className={`rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              setupStatus.modes.stripe === "live"
                ? "bg-rose-50 border-rose-300 text-rose-700"
                : setupStatus.modes.stripe === "test"
                ? "bg-amber-50 border-amber-300 text-amber-800"
                : "bg-neutral-100 border-neutral-300 text-neutral-600"
            }`}
            data-testid="stripe-mode-banner"
            title={setupStatus.modes.stripe === "live" ? "Stripe is in LIVE mode — real charges" : "Stripe is in TEST mode — no real money"}
          >
            {setupStatus.modes.stripe === "live" ? "● Stripe LIVE" : setupStatus.modes.stripe === "test" ? "● Stripe TEST" : "● Stripe not set"}
          </div>
        )}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-blush/40 rounded-full p-1">
          <TabsTrigger value="overview" className="rounded-full px-5" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sales" className="rounded-full px-5" data-testid="admin-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="events" className="rounded-full px-5" data-testid="admin-tab-events">Events</TabsTrigger>
          <TabsTrigger value="content" className="rounded-full px-5" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="attendees" className="rounded-full px-5" data-testid="admin-tab-attendees">Attendees</TabsTrigger>
          <TabsTrigger value="waitlist" className="rounded-full px-5" data-testid="admin-tab-waitlist">Waitlist</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full px-5" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="promo" className="rounded-full px-5" data-testid="admin-tab-promo">Promo codes</TabsTrigger>
          <TabsTrigger value="email-log" className="rounded-full px-5" data-testid="admin-tab-email-log">Email log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="overview-metrics">
            <Metric label="Total revenue" value={`$${((analytics.revenue_cents||0)/100).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2})}`} data-testid="metric-revenue" />
            <Metric label="Tickets sold" value={analytics.tickets_sold || 0} data-testid="metric-tickets-sold" />
            <Metric label="Avg ticket" value={`$${((analytics.avg_ticket_cents||0)/100).toFixed(0)}`} data-testid="metric-avg-ticket" />
            <Metric label="Refunds" value={analytics.refunds || 0} data-testid="metric-refunds" />
            <Metric label="Upcoming events" value={analytics.upcoming_events || 0} data-testid="metric-upcoming" />
          </div>

          {/* Recent purchases */}
          <div className="mt-8 card-elegant overflow-hidden" data-testid="recent-purchases">
            <div className="flex items-baseline justify-between p-6 pb-3">
              <div>
                <div className="text-eyebrow">Recent purchases</div>
                <div className="font-heading text-xl mt-1">Latest 20 paid tickets</div>
              </div>
              <button onClick={()=>setTab("sales")} className="text-xs underline underline-offset-4 hover:opacity-70" data-testid="recent-view-all">See full sales →</button>
            </div>
            {recent.length === 0 ? (
              <div className="px-6 py-8 text-sm text-neutral-500">No purchases yet.</div>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Buyer</TableHead><TableHead>Event</TableHead><TableHead>Ticket</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {recent.map(r => (
                    <TableRow key={r.ticket_id} data-testid={`recent-row-${r.ticket_id}`}>
                      <TableCell className="text-xs text-neutral-500">{r.created_at ? new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</TableCell>
                      <TableCell>
                        <div className="font-medium">{r.buyer_name || "—"}</div>
                        <a href={`/admin/users/${r.buyer_user_id}`} className="text-xs text-neutral-500 hover:text-black underline underline-offset-2">{r.buyer_email}</a>
                      </TableCell>
                      <TableCell className="text-sm">{r.event_title || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.ticket_code}</TableCell>
                      <TableCell className="text-right font-medium">${((r.amount_cents||0)/100).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Setup checklist */}
          {setupStatus && (
            <div className="mt-8 card-elegant p-6" data-testid="setup-checklist">
              <div className="flex items-baseline justify-between gap-4 mb-5">
                <div>
                  <div className="text-eyebrow">Launch readiness</div>
                  <div className="font-heading text-xl mt-1">{setupStatus.summary.ok} of {setupStatus.summary.total} integrations ready</div>
                </div>
                <div className="text-xs text-neutral-500">Live status</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {setupStatus.checks.map(c => (
                  <div key={c.id} className="flex items-start gap-3 rounded-2xl border border-neutral-200 p-4 bg-white/60" data-testid={`setup-check-${c.id}`}>
                    <span className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      c.status === "ok" ? "bg-emerald-100 text-emerald-800" :
                      c.status === "pending" ? "bg-amber-100 text-amber-800" :
                      "bg-rose-100 text-rose-800"
                    }`}>
                      {c.status === "ok" ? "✓" : c.status === "pending" ? "…" : "!"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm">{c.label}</div>
                      <div className="text-xs text-neutral-600 mt-0.5">{c.detail}</div>
                      {c.action_url && c.status !== "ok" && (
                        <a href={c.action_url} target="_blank" rel="noreferrer" className="text-xs text-black underline mt-1 inline-block hover:opacity-70" data-testid={`setup-fix-${c.id}`}>Fix now →</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 card-elegant p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-eyebrow">Automated reminders</div>
              <div className="font-heading text-xl mt-1">24-hour event reminders</div>
              <div className="text-sm text-neutral-600 mt-1">Runs automatically every 30 minutes. Trigger manually if needed.</div>
            </div>
            <Button
              onClick={async ()=>{ try { const {data} = await api.post("/admin/reminders/run"); toast.success(`Reminders: ${data.sent} sent, ${data.errors} errored, ${data.events_checked} events checked`); } catch { toast.error("Reminder run failed"); } }}
              className="rounded-full bg-black text-white btn-pill self-start"
              data-testid="run-reminders-btn"
            >
              Run reminders now
            </Button>
          </div>

          {/* Checkout payment methods */}
          <div className="mt-6 card-elegant p-6" data-testid="payment-methods-card">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-eyebrow">Stripe checkout</div>
                <div className="font-heading text-xl mt-1">Payment methods</div>
                <div className="text-sm text-neutral-600 mt-1">Toggle which options show at checkout. Turn off in one click if a provider gives you trouble.</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {(paymentMethods.available || []).map(m => {
                const on = (paymentMethods.methods || []).includes(m);
                const locked = m === "card";
                return (
                  <label
                    key={m}
                    className={`flex items-start gap-3 rounded-2xl border p-4 transition ${on ? "bg-white border-neutral-300" : "bg-neutral-50 border-neutral-200"} ${locked ? "opacity-90 cursor-not-allowed" : "cursor-pointer hover:border-black"}`}
                    data-testid={`pm-${m}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={pmSaving || locked}
                      onChange={()=>togglePaymentMethod(m)}
                      className="h-4 w-4 accent-black mt-0.5"
                      data-testid={`pm-toggle-${m}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm flex items-center gap-2">
                        {PAYMENT_METHOD_LABELS[m] || m}
                        {locked && <span className="text-[10px] font-mono uppercase tracking-widest bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded">always on</span>}
                        {!locked && on && <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">live</span>}
                      </div>
                      <div className="text-xs text-neutral-600 mt-1">{PAYMENT_METHOD_DESCRIPTIONS[m] || ""}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="text-xs text-neutral-500 mt-4">
              Method must also be enabled in your Stripe dashboard under <span className="font-mono">Settings → Payment methods</span>. Toggling here only controls what Offline &amp; Available offers at checkout.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="events">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) setEditingId(null); }}>
              <DialogTrigger asChild><Button onClick={openCreate} className="rounded-full bg-black text-white" data-testid="new-event-btn">+ New event</Button></DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{editingId ? "Edit event" : "Create event"}</DialogTitle>
                  <DialogDescription className="text-sm text-neutral-500">Set the event details, upload a photo, and edit the schedule + FAQ shown to attendees.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Title" value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className="col-span-2 rounded-xl" data-testid="ev-title" />
                  <Textarea rows={3} placeholder="Description" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="col-span-2 rounded-xl" data-testid="ev-desc" />
                  <Input placeholder="Venue" value={form.venue} onChange={e=>setForm({...form, venue: e.target.value})} className="rounded-xl" data-testid="ev-venue" />
                  <Input placeholder="Address" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} className="rounded-xl" data-testid="ev-address" />
                  <Input type="date" value={form.date} onChange={e=>setForm({...form, date: e.target.value})} className="rounded-xl" data-testid="ev-date" />
                  <Input placeholder="Time (e.g. 7:30 PM)" value={form.time} onChange={e=>setForm({...form, time: e.target.value})} className="rounded-xl" data-testid="ev-time" />
                  <Input type="number" placeholder="Age min" value={form.age_min} onChange={e=>setForm({...form, age_min: e.target.value})} className="rounded-xl" />
                  <Input type="number" placeholder="Age max" value={form.age_max} onChange={e=>setForm({...form, age_max: e.target.value})} className="rounded-xl" />
                  <Input type="number" placeholder="Price (cents — 5000 = $50.00)" value={form.price_cents} onChange={e=>setForm({...form, price_cents: e.target.value})} className="rounded-xl" data-testid="ev-price" />
                  <Input type="number" placeholder="Capacity" value={form.capacity} onChange={e=>setForm({...form, capacity: e.target.value})} className="rounded-xl" data-testid="ev-capacity" />
                  {/* Image upload + focal point repositioning */}
                  <div className="col-span-2 rounded-2xl border border-neutral-200 p-4 bg-blush/20">
                    <div className="flex gap-4 items-start">
                      <div
                        className="relative w-40 h-40 rounded-xl bg-neutral-100 overflow-hidden shrink-0 cursor-crosshair select-none"
                        data-testid="ev-photo-focal-preview"
                        onClick={(evt) => {
                          if (!form.image_url) return;
                          const r = evt.currentTarget.getBoundingClientRect();
                          const x = Math.round(((evt.clientX - r.left) / r.width) * 100);
                          const y = Math.round(((evt.clientY - r.top) / r.height) * 100);
                          setForm(f => ({ ...f, image_focal_x: Math.max(0, Math.min(100, x)), image_focal_y: Math.max(0, Math.min(100, y)) }));
                        }}
                      >
                        {form.image_url ? (
                          <>
                            <img
                              src={form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url}
                              alt=""
                              className="w-full h-full object-cover"
                              style={{ objectPosition: `${form.image_focal_x ?? 50}% ${form.image_focal_y ?? 50}%` }}
                            />
                            <div
                              className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white border-2 border-black shadow-lg pointer-events-none"
                              style={{ left: `${form.image_focal_x ?? 50}%`, top: `${form.image_focal_y ?? 50}%` }}
                              data-testid="ev-photo-focal-dot"
                            />
                          </>
                        ) : <div className="w-full h-full flex items-center justify-center text-xs text-neutral-500">No image</div>}
                      </div>
                      <div className="flex-1">
                        <div className="text-eyebrow">Event photo</div>
                        <div className="text-xs text-neutral-500 mt-1">JPG, PNG, or WEBP · up to 5 MB</div>
                        <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadEventPhoto} className="hidden" data-testid="ev-photo-input" />
                        <Button type="button" onClick={()=>editingId ? photoInputRef.current?.click() : toast.info("Save the event first, then upload a photo")} disabled={photoBusy} variant="outline" className="mt-2 rounded-full" data-testid="ev-photo-upload-btn">
                          <Upload size={14} className="mr-2" /> {photoBusy ? "Uploading…" : (form.image_url ? "Change photo" : "Upload photo")}
                        </Button>
                        <div className="mt-3 text-xs text-neutral-600">
                          Click the preview to set the <strong>focal point</strong> — that spot stays centered when the photo fills event cards and hero banners.
                          <div className="mt-2 flex items-center gap-2 text-[11px] font-mono text-neutral-500">
                            <span>x: {form.image_focal_x ?? 50}%</span>
                            <span>y: {form.image_focal_y ?? 50}%</span>
                            <Button size="sm" variant="ghost" type="button" onClick={()=>setForm(f=>({...f, image_focal_x: 50, image_focal_y: 50}))} className="h-6 px-2 text-[11px]" data-testid="ev-photo-focal-reset">Center</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Input placeholder="Parking info" value={form.parking_info} onChange={e=>setForm({...form, parking_info: e.target.value})} className="col-span-2 rounded-xl" />
                  <Input placeholder="Dress code" value={form.dress_code} onChange={e=>setForm({...form, dress_code: e.target.value})} className="col-span-2 rounded-xl" />
                  <select value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="col-span-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="ev-category">
                    <option>Singles</option><option>Couples</option><option>Friends</option><option>Special</option>
                  </select>

                  {/* Publish toggle */}
                  <label className="col-span-2 flex items-center gap-3 text-sm cursor-pointer bg-white rounded-xl border border-neutral-200 px-4 py-3">
                    <input type="checkbox" checked={form.is_published !== false} onChange={e=>setForm({...form, is_published: e.target.checked})} data-testid="ev-published" className="h-4 w-4 accent-black" />
                    <span><strong>{form.is_published !== false ? "Published" : "Draft"}</strong> — {form.is_published !== false ? "visible on the public events page" : "hidden from the public (admins only)"}</span>
                  </label>

                  {/* Registration open toggle */}
                  <label className="col-span-2 flex items-center gap-3 text-sm cursor-pointer bg-white rounded-xl border border-neutral-200 px-4 py-3">
                    <input type="checkbox" checked={form.registration_open !== false} onChange={e=>setForm({...form, registration_open: e.target.checked})} data-testid="ev-registration-open" className="h-4 w-4 accent-black" />
                    <span><strong>Registration {form.registration_open !== false ? "open" : "closed"}</strong> — {form.registration_open !== false ? "guests can buy tickets" : "event stays visible but the buy button is disabled"}</span>
                  </label>

                  {/* Mark as Full override */}
                  <label className="col-span-2 flex items-center gap-3 text-sm cursor-pointer bg-white rounded-xl border border-neutral-200 px-4 py-3">
                    <input type="checkbox" checked={!!form.force_full} onChange={e=>setForm({...form, force_full: e.target.checked})} data-testid="ev-force-full" className="h-4 w-4 accent-black" />
                    <span><strong>Mark as full</strong> — force "Sold out" even if capacity remains (auto-applied when tickets sold ≥ capacity)</span>
                  </label>

                  {/* Waitlist toggle */}
                  <label className="col-span-2 flex items-center gap-3 text-sm cursor-pointer bg-white rounded-xl border border-neutral-200 px-4 py-3">
                    <input type="checkbox" checked={form.allow_waitlist_when_full !== false} onChange={e=>setForm({...form, allow_waitlist_when_full: e.target.checked})} data-testid="ev-allow-waitlist" className="h-4 w-4 accent-black" />
                    <span><strong>Allow waitlist when full</strong> — show a "Join waitlist" button once the event is sold out</span>
                  </label>

                  {/* Schedule editor */}
                  <ItemListEditor
                    title="Schedule"
                    itemsKey="schedule"
                    items={form.schedule || []}
                    onChange={(next)=>setForm({...form, schedule: next})}
                    fields={[
                      { key: "time", label: "Time (e.g. 7:30 PM)" },
                      { key: "title", label: "Title" },
                      { key: "description", label: "Description", textarea: true },
                    ]}
                  />

                  {/* FAQ editor */}
                  <ItemListEditor
                    title="FAQ"
                    itemsKey="faq"
                    items={form.faq || []}
                    onChange={(next)=>setForm({...form, faq: next})}
                    fields={[
                      { key: "question", label: "Question" },
                      { key: "answer", label: "Answer", textarea: true },
                    ]}
                  />

                  <Button onClick={saveEvent} className="col-span-2 rounded-full bg-black text-white btn-pill" data-testid="ev-save-btn">{editingId ? "Save changes" : "Create"}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="card-elegant overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Date</TableHead><TableHead>Venue</TableHead><TableHead>Price</TableHead><TableHead>Sold</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {events.map(e => {
                  const regOpen = e.registration_open !== false;
                  const isFull = e.is_full ?? (e.spots_remaining === 0);
                  const forceFull = !!e.force_full;
                  return (
                  <TableRow key={e.event_id} data-testid={`admin-event-row-${e.event_id}`}>
                    <TableCell className="font-medium">
                      {e.title}
                      {e.is_published === false && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Draft</span>}
                      {!regOpen && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded" data-testid={`row-reg-closed-${e.event_id}`}>Reg closed</span>}
                      {forceFull && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-black text-white px-1.5 py-0.5 rounded" data-testid={`row-forced-full-${e.event_id}`}>Marked full</span>}
                      {!forceFull && isFull && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded">Sold out</span>}
                    </TableCell>
                    <TableCell>{e.category || "—"}</TableCell>
                    <TableCell>{formatEventDate(e.date, { month: "short", day: "numeric", year: "numeric" })}</TableCell>
                    <TableCell>{e.venue}</TableCell>
                    <TableCell>${(e.price_cents/100).toFixed(0)}</TableCell>
                    <TableCell>
                      <button
                        onClick={()=>promptCapacity(e)}
                        className="hover:underline decoration-dotted underline-offset-2"
                        title="Click to change capacity"
                        data-testid={`edit-capacity-${e.event_id}`}
                      >
                        {e.tickets_sold}/{e.capacity}
                      </button>
                    </TableCell>
                    <TableCell>{e.status}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={()=>openEdit(e)} data-testid={`edit-event-${e.event_id}`} title="Edit"><Pencil size={12} /></Button>
                      <Button size="sm" variant="outline" onClick={()=>togglePublish(e.event_id, e.is_published !== false)} data-testid={`publish-event-${e.event_id}`} title={e.is_published === false ? "Publish (show publicly)" : "Move to draft (hide from public)"}>
                        {e.is_published === false ? <EyeOff size={12} /> : <Eye size={12} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>patchRegistration(e.event_id, { registration_open: !regOpen }, !regOpen ? "Registration opened" : "Registration closed")} data-testid={`toggle-registration-${e.event_id}`} title={regOpen ? "Close registration (keep event visible)" : "Open registration"}>
                        {regOpen ? <Unlock size={12} /> : <Lock size={12} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>patchRegistration(e.event_id, { force_full: !forceFull }, !forceFull ? "Marked as full" : "Full override removed")} data-testid={`toggle-full-${e.event_id}`} title={forceFull ? "Remove manual full override" : "Mark as full (Sold out)"}>
                        <Ban size={12} className={forceFull ? "text-red-600" : ""} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>duplicateEvent(e.event_id)} data-testid={`duplicate-event-${e.event_id}`} title="Duplicate"><Copy size={12} /></Button>
                      <Button size="sm" variant="outline" onClick={()=>{ viewAttendees(e.event_id); setTab("attendees"); }} data-testid={`view-attendees-${e.event_id}`}>Attendees</Button>
                      <Button size="sm" variant="outline" onClick={()=>{ viewWaitlist(e.event_id); setTab("waitlist"); }} data-testid={`view-waitlist-${e.event_id}`}>Waitlist</Button>
                      <Button asChild size="sm" variant="outline" data-testid={`door-mode-${e.event_id}`} title="Open full-screen door check-in on your phone">
                        <Link to={`/admin/events/${e.event_id}/door`}>Door</Link>
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>deleteEvent(e.event_id, e.tickets_sold > 0)} data-testid={`delete-event-${e.event_id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" title={e.tickets_sold > 0 ? "Refund all + delete" : "Delete"}><Trash2 size={12} /></Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="content">
          {/* Homepage section visibility — toggle any section on/off without touching code */}
          <div className="card-elegant p-6 mb-6" data-testid="home-sections-card">
            <div className="text-eyebrow">Homepage sections</div>
            <h2 className="font-heading text-2xl mt-1">Reorder and toggle sections on your homepage</h2>
            <p className="text-sm text-neutral-600 mt-2">Use the arrows to reorder sections, or flip any section off to hide it. Copy and images stay saved.</p>
            {(() => {
              const DEFAULT_ORDER = ["upcoming", "experiences", "story", "connect", "manifesto"];
              const orderRow = cms.find(r => r.key === "home.sections.order");
              const orderList = (orderRow?.value || DEFAULT_ORDER.join(","))
                .split(",").map(s => s.trim()).filter(Boolean);
              const fullOrder = [...orderList, ...DEFAULT_ORDER.filter(s => !orderList.includes(s))];
              const setOrder = (next) => saveCms("home.sections.order", next.join(","));
              const move = (name, dir) => {
                const i = fullOrder.indexOf(name);
                const j = i + dir;
                if (i < 0 || j < 0 || j >= fullOrder.length) return;
                const next = [...fullOrder];
                [next[i], next[j]] = [next[j], next[i]];
                setOrder(next);
              };
              return (
                <div className="mt-6 space-y-3" data-testid="section-reorder-list">
                  {fullOrder.map((name, idx) => {
                    const toggleKey = `home.sections.${name}.visible`;
                    const toggleRow = cms.find(r => r.key === toggleKey);
                    const on = (toggleRow?.value || "true") !== "false";
                    const label = SECTION_LABELS[toggleKey] || { title: name, description: "" };
                    return (
                      <div
                        key={name}
                        className={`flex items-center gap-3 rounded-2xl border p-4 transition ${on ? "bg-white border-neutral-300" : "bg-neutral-50 border-neutral-200"}`}
                        data-testid={`section-row-${name}`}
                      >
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={()=>move(name, -1)}
                            disabled={idx === 0 || cmsSaving === "home.sections.order"}
                            className="w-7 h-6 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-xs"
                            data-testid={`section-up-${name}`}
                            aria-label="Move up"
                          >↑</button>
                          <button
                            onClick={()=>move(name, 1)}
                            disabled={idx === fullOrder.length - 1 || cmsSaving === "home.sections.order"}
                            className="w-7 h-6 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-xs"
                            data-testid={`section-down-${name}`}
                            aria-label="Move down"
                          >↓</button>
                        </div>
                        <div className="font-mono text-xs text-neutral-400 w-6 tabular-nums shrink-0">#{idx + 1}</div>
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={cmsSaving === toggleKey}
                          onChange={()=>saveCms(toggleKey, on ? "false" : "true")}
                          className="h-4 w-4 accent-black shrink-0"
                          data-testid={`section-toggle-input-${toggleKey}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-sm flex items-center gap-2">
                            {label.title}
                            {on ? <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">visible</span>
                                : <span className="text-[10px] font-mono uppercase tracking-widest bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded">hidden</span>}
                          </div>
                          <div className="text-xs text-neutral-600 mt-0.5">{label.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div className="card-elegant p-6">
            <div className="text-eyebrow">Website content management</div>
            <h2 className="font-heading text-2xl mt-1">Edit every headline, subheadline, and image — live.</h2>
            <p className="text-sm text-neutral-600 mt-2">Changes save immediately and show up on the site as soon as visitors refresh. Use "Reset" to restore the default copy.</p>
            <div className="mt-6 space-y-4">
              {cms.filter(r => r.kind !== "toggle" && r.kind !== "order").map(row => (
                <CmsRow
                  key={row.key}
                  row={row}
                  busy={cmsSaving === row.key}
                  onSave={saveCms}
                  onReset={resetCms}
                  onUploadImage={uploadCmsImage}
                />
              ))}
              {cms.length === 0 && <div className="text-neutral-500 text-sm">Loading content…</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="attendees">
          {!selEvent ? <div className="card-elegant p-12 text-center text-neutral-500">Pick an event from the Events tab to view attendees.</div> : (
            <>
              <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-eyebrow">Attendees</div>
                  <div className="text-sm text-neutral-600 mt-1">{attendees.filter(a=>a.status==="paid").length} paid · {attendees.filter(a=>a.checked_in_at).length} checked in · {attendees.length} total</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={()=>setAddAttendeeOpen(true)} className="rounded-full" data-testid="add-attendee-btn"><UserPlus size={14} className="mr-2" /> Add attendee</Button>
                  <Button variant="outline" onClick={exportAttendeesCsv} disabled={attendees.length===0} className="rounded-full" data-testid="export-attendees-csv"><Download size={14} className="mr-2" /> Export CSV</Button>
                  <Button variant="outline" onClick={resendAllConfirmations} disabled={attendees.filter(a=>a.status==="paid").length===0} className="rounded-full" data-testid="resend-all-qr-btn" title="Resend branded confirmation (with QR) to every paid attendee — great for backfilling pre-QR tickets"><QrCode size={14} className="mr-2" /> Resend QR to all</Button>
                  <Button variant="outline" onClick={()=>setEmailEventOpen(true)} disabled={attendees.filter(a=>a.status==="paid").length===0} className="rounded-full" data-testid="email-all-attendees-btn"><Mail size={14} className="mr-2" /> Email attendees</Button>
                </div>
              </div>
              <div className="card-elegant overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Age</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Ticket</TableHead><TableHead>Qty</TableHead><TableHead>Amount</TableHead><TableHead>Checked in</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {attendees.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-8">No attendees yet. Use <strong>Add attendee</strong> to comp someone in.</TableCell></TableRow> : attendees.map(a => (
                      <TableRow key={a.ticket_id} data-testid={`attendee-row-${a.ticket_id}`} className={a.checked_in_at ? "bg-emerald-50/40" : ""}>
                        <TableCell><Link to={`/admin/users/${a.user?.user_id}`} className="hover:underline">{a.user?.name}</Link>{a.admin_note && <span className="ml-1 text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded align-middle" title={a.admin_note}>comp</span>}</TableCell>
                        <TableCell className="tabular-nums" data-testid={`attendee-age-${a.ticket_id}`}>{a.user?.age ?? "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{a.user?.email}</TableCell>
                        <TableCell className="font-mono text-xs">{a.user?.phone || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{a.ticket_code}</TableCell>
                        <TableCell>{a.quantity}</TableCell>
                        <TableCell>${(a.amount/100).toFixed(2)}</TableCell>
                        <TableCell>
                          {a.status === "paid" ? (
                            <Button size="sm" variant={a.checked_in_at ? "default" : "outline"} onClick={()=>toggleCheckIn(a)} data-testid={`checkin-${a.ticket_id}`} className={a.checked_in_at ? "bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-7 px-3" : "rounded-full h-7 px-3"} title={a.checked_in_at ? `Checked in ${new Date(a.checked_in_at).toLocaleString()} · Click to undo` : "Mark checked in"}>
                              {a.checked_in_at ? <><CheckCircle2 size={12} className="mr-1" /> In</> : <><CircleDashed size={12} className="mr-1" /> Check in</>}
                            </Button>
                          ) : (
                            <span className="text-neutral-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {a.status === "paid" && (
                            <>
                              <Button size="sm" variant="outline" onClick={()=>setConfViewerTicketId(a.ticket_id)} data-testid={`view-conf-${a.ticket_id}`} title="View / copy / resend confirmation"><Mail size={12} /></Button>
                              {a.amount > 0 && <Button size="sm" variant="outline" onClick={()=>refund(a.ticket_id)} data-testid={`refund-${a.ticket_id}`}>Refund</Button>}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="waitlist">
          {!selEvent ? <div className="card-elegant p-12 text-center text-neutral-500">Pick an event from the Events tab, then click <strong>Waitlist</strong> on its row.</div> : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-eyebrow">Waitlist</div>
                  <div className="text-sm text-neutral-600 mt-1">{waitlist.length} on the waitlist · promote to comp a free ticket + email their confirmation</div>
                </div>
              </div>
              <div className="card-elegant overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Joined</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {waitlist.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-8">Nobody's on the waitlist yet.</TableCell></TableRow> : waitlist.map(w => (
                      <TableRow key={w.waitlist_id || w.user_id} data-testid={`waitlist-row-${w.waitlist_id || w.user_id}`}>
                        <TableCell><Link to={`/admin/users/${w.user?.user_id}`} className="hover:underline">{w.user?.name || "—"}</Link></TableCell>
                        <TableCell className="font-mono text-xs">{w.user?.email}</TableCell>
                        <TableCell className="font-mono text-xs">{w.user?.phone || "—"}</TableCell>
                        <TableCell className="text-xs text-neutral-600">{w.created_at ? new Date(w.created_at).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={()=>promoteWaitlistEntry(w)} data-testid={`promote-wl-${w.waitlist_id || w.user_id}`} title="Comp a ticket + email confirmation">Promote</Button>
                          <Button size="sm" variant="outline" onClick={()=>removeWaitlistEntry(w)} data-testid={`remove-wl-${w.waitlist_id || w.user_id}`} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12} /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="users">
          <UsersTab
            users={users}
            showDeleted={showDeleted}
            onToggleShowDeleted={()=>setShowDeleted(v=>!v)}
            onUpdate={updateUser}
            onSuspend={suspendUser}
            onGrantVip={grantVip}
            onSendReset={sendResetForUser}
            onBroadcast={()=>setBroadcastOpen(true)}
            onDelete={deleteUser}
            onRestore={restoreUser}
          />
        </TabsContent>

        <TabsContent value="promo">
          <PromoCodesTab events={events} users={users} />
        </TabsContent>

        <TabsContent value="email-log">
          <EmailLogTab />
        </TabsContent>

        <TabsContent value="sales">
          <SalesTab />
        </TabsContent>
      </Tabs>

      <ComposeEmailDialog
        open={emailEventOpen}
        onOpenChange={setEmailEventOpen}
        mode="event"
        targetId={selEvent}
        recipientLabel={events.find(e=>e.event_id===selEvent)?.title || "this event's paid attendees"}
        recipientCount={attendees.filter(a=>a.status==="paid").length}
        testId="compose-event-email"
      />
      <ComposeEmailDialog
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        mode="broadcast"
        recipientLabel="all active users"
        recipientCount={users.filter(u=>!u.suspended).length}
        testId="compose-broadcast"
      />
      <ConfirmationViewerDialog
        open={!!confViewerTicketId}
        onOpenChange={(v)=>{ if (!v) setConfViewerTicketId(null); }}
        ticketId={confViewerTicketId}
      />
      <AddAttendeeDialog
        open={addAttendeeOpen}
        onOpenChange={setAddAttendeeOpen}
        eventId={selEvent}
        eventTitle={events.find(e=>e.event_id===selEvent)?.title}
        onCreated={()=>{ viewAttendees(selEvent); load(); }}
      />
    </div>
  );
}

function UsersTab({ users, onUpdate, onSuspend, onGrantVip, onSendReset, onBroadcast, onDelete, onRestore, showDeleted, onToggleShowDeleted }) {
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // user object or null
  const [draft, setDraft] = useState({});

  const filtered = users.filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });
  const deletedCount = users.filter(u => !!u.deleted_at).length;

  const openEditUser = (u) => {
    setEditing(u);
    setDraft({ name: u.name || "", email: u.email, city: u.city || "", role: u.role || "user" });
  };
  const saveUser = async () => {
    await onUpdate(editing.user_id, draft);
    setEditing(null);
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search by name or email…"
          value={query}
          onChange={e=>setQuery(e.target.value)}
          className="max-w-sm rounded-xl"
          data-testid="admin-users-search"
        />
        <span className="text-xs text-neutral-500">{filtered.length} of {users.length}{showDeleted && deletedCount ? ` · ${deletedCount} deleted` : ""}</span>
        <label className="flex items-center gap-2 text-xs cursor-pointer text-neutral-700">
          <input type="checkbox" checked={showDeleted} onChange={onToggleShowDeleted} className="h-4 w-4 accent-black" data-testid="users-show-deleted" />
          Show deleted
        </label>
        <Button variant="outline" onClick={onBroadcast} className="ml-auto rounded-full" data-testid="broadcast-all-users-btn"><Mail size={14} className="mr-2" /> Email all users</Button>
      </div>
      <div className="card-elegant overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>VIP</TableHead><TableHead>Joined</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className={u.deleted_at ? "opacity-50 bg-rose-50/30" : u.suspended ? "opacity-60" : ""}>
                <TableCell>{u.name}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell>{u.role}</TableCell>
                <TableCell>{u.is_vip ? <span className="text-emerald-700 font-medium">VIP</span> : <span className="text-neutral-400">—</span>}</TableCell>
                <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  {u.deleted_at ? <span className="text-rose-700 font-medium" title={`Deleted ${new Date(u.deleted_at).toLocaleString()}`}>Deleted</span>
                   : u.suspended ? <span className="text-rose-700 font-medium">Suspended</span>
                   : "Active"}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {u.deleted_at ? (
                    <>
                      <Button size="sm" variant="outline" onClick={()=>onRestore(u)} data-testid={`user-restore-${u.user_id}`} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">Restore</Button>
                    </>
                  ) : (
                    <>
                      <Button asChild size="sm" variant="outline" data-testid={`user-view-${u.user_id}`} title="View full profile">
                        <Link to={`/admin/users/${u.user_id}`}><ExternalLink size={12} /></Link>
                      </Button>
                      <Button size="sm" variant="outline" onClick={()=>openEditUser(u)} data-testid={`user-edit-${u.user_id}`} title="Edit"><Pencil size={12} /></Button>
                      <Button size="sm" variant="outline" onClick={()=>onGrantVip(u.user_id, !!u.is_vip)} data-testid={`user-vip-${u.user_id}`} title={u.is_vip ? "Revoke VIP" : "Grant VIP"}>{u.is_vip ? "-VIP" : "+VIP"}</Button>
                      <Button size="sm" variant="outline" onClick={()=>onSendReset(u.user_id, u.email)} data-testid={`user-reset-${u.user_id}`} title="Send password reset">Reset</Button>
                      <Button size="sm" variant="outline" onClick={()=>onSuspend(u.user_id, !!u.suspended)} data-testid={`user-suspend-${u.user_id}`} className={u.suspended ? "" : "text-rose-600 border-rose-200 hover:bg-rose-50"}>{u.suspended ? "Reactivate" : "Suspend"}</Button>
                      <Button size="sm" variant="outline" onClick={()=>onDelete(u)} data-testid={`user-delete-${u.user_id}`} className="text-rose-700 border-rose-200 hover:bg-rose-50" title="Delete user (soft) — keeps their tickets on file"><Trash2 size={12} /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-8">No users match your search.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v)=>{ if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit user</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">Update this user's profile fields. Changes take effect immediately.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <div className="text-eyebrow mb-1">Name</div>
                <Input value={draft.name} onChange={e=>setDraft({...draft, name: e.target.value})} className="rounded-xl" data-testid="user-edit-name" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Email</div>
                <Input type="email" value={draft.email} onChange={e=>setDraft({...draft, email: e.target.value})} className="rounded-xl" data-testid="user-edit-email" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">City</div>
                <Input value={draft.city} onChange={e=>setDraft({...draft, city: e.target.value})} className="rounded-xl" data-testid="user-edit-city" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Role</div>
                <select value={draft.role} onChange={e=>setDraft({...draft, role: e.target.value})} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="user-edit-role">
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              <Button onClick={saveUser} className="w-full rounded-full bg-black text-white btn-pill" data-testid="user-edit-save">Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ItemListEditor({ title, itemsKey, items, onChange, fields }) {
  // Ensure every row has a stable synthetic id so React can reconcile correctly
  // when rows are reordered (avoids array-index-as-key reconciliation bugs).
  const withKeys = items.map(it => (it && it._key ? it : { ...it, _key: `row_${Math.random().toString(36).slice(2, 10)}` }));
  const add = () => {
    const blank = fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
    blank._key = `row_${Math.random().toString(36).slice(2, 10)}`;
    onChange([...withKeys, blank]);
  };
  const update = (idx, key, value) => {
    const next = withKeys.slice();
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    if (!window.confirm("Remove this item?")) return;
    onChange(withKeys.filter((_, i) => i !== idx));
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= withKeys.length) return;
    const next = withKeys.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="col-span-2 rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`ev-${itemsKey}-editor`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-eyebrow">{title}</div>
        <Button type="button" size="sm" variant="outline" onClick={add} className="rounded-full h-8" data-testid={`ev-${itemsKey}-add`}><Plus size={12} className="mr-1" /> Add row</Button>
      </div>
      {withKeys.length === 0 ? (
        <div className="text-xs text-neutral-500 py-2">No rows yet. Click "Add row" to build the {title.toLowerCase()}.</div>
      ) : (
        <div className="space-y-3">
          {withKeys.map((it, idx) => (
            <div key={it._key} className="rounded-xl border border-neutral-200 p-3 bg-neutral-50" data-testid={`ev-${itemsKey}-row-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1 text-neutral-400"><GripVertical size={14} /> <span className="text-xs">#{idx + 1}</span></div>
                <div className="flex items-center gap-1">
                  <Button type="button" size="sm" variant="ghost" onClick={()=>move(idx, -1)} disabled={idx===0} className="h-7 px-2 text-xs">↑</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={()=>move(idx, 1)} disabled={idx===withKeys.length-1} className="h-7 px-2 text-xs">↓</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={()=>remove(idx)} className="h-7 px-2 text-rose-600" data-testid={`ev-${itemsKey}-remove-${idx}`}><Trash2 size={12} /></Button>
                </div>
              </div>
              <div className="grid gap-2">
                {fields.map(f => f.textarea ? (
                  <Textarea key={f.key} rows={2} placeholder={f.label} value={it[f.key] || ""} onChange={e=>update(idx, f.key, e.target.value)} className="rounded-xl text-sm" data-testid={`ev-${itemsKey}-${idx}-${f.key}`} />
                ) : (
                  <Input key={f.key} placeholder={f.label} value={it[f.key] || ""} onChange={e=>update(idx, f.key, e.target.value)} className="rounded-xl text-sm" data-testid={`ev-${itemsKey}-${idx}-${f.key}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, value, "data-testid": testId }) => (
  <div className="card-elegant p-6" data-testid={testId || `metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-eyebrow">{label}</div>
    <div className="font-heading text-3xl mt-2">{value}</div>
  </div>
);


function CmsRow({ row, busy, onSave, onReset, onUploadImage }) {
  const [draft, setDraft] = useState(row.value);
  useEffect(() => { setDraft(row.value); }, [row.value]);
  const changed = draft !== row.value;
  const isImage = row.kind === "image";
  const imgUrl = draft?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${draft}` : draft;

  return (
    <div className="rounded-2xl border border-neutral-200 p-4 bg-white/70" data-testid={`cms-row-${row.key}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">{row.key}</div>
          <div className="text-[10px] text-neutral-400 mt-0.5">{isImage ? "Image URL" : "Text"}{row.is_customized && " · customized"}</div>
        </div>
        {row.is_customized && (
          <Button size="sm" variant="ghost" onClick={()=>onReset(row.key)} disabled={busy} data-testid={`cms-reset-${row.key}`}>
            <RotateCcw size={12} className="mr-1" /> Reset
          </Button>
        )}
      </div>

      {isImage ? (
        <div className="mt-3 flex gap-4 items-start">
          <div className="w-32 h-24 rounded-lg overflow-hidden bg-neutral-100 shrink-0">
            {imgUrl ? <img src={imgUrl} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <div className="flex-1 space-y-2">
            <Input value={draft} onChange={e=>setDraft(e.target.value)} placeholder="Image URL" className="rounded-xl text-xs" data-testid={`cms-input-${row.key}`} />
            <div className="flex gap-2 flex-wrap">
              <label className="cursor-pointer">
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e=>{ const f=e.target.files?.[0]; if (f) onUploadImage(row.key, f); e.target.value=""; }} className="hidden" data-testid={`cms-upload-${row.key}`} />
                <span className="inline-flex items-center gap-1 rounded-full bg-black text-white text-xs px-3 py-1.5 hover:opacity-90"><Upload size={12} /> Upload new</span>
              </label>
              {changed && <Button size="sm" onClick={()=>onSave(row.key, draft)} disabled={busy} className="rounded-full bg-neutral-900 text-white" data-testid={`cms-save-${row.key}`}>Save URL</Button>}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {draft.length > 80 ? (
            <Textarea rows={3} value={draft} onChange={e=>setDraft(e.target.value)} className="rounded-xl" data-testid={`cms-input-${row.key}`} />
          ) : (
            <Input value={draft} onChange={e=>setDraft(e.target.value)} className="rounded-xl" data-testid={`cms-input-${row.key}`} />
          )}
          {changed && (
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>onSave(row.key, draft)} disabled={busy} className="rounded-full bg-black text-white" data-testid={`cms-save-${row.key}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={()=>setDraft(row.value)}>Cancel</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function PromoCodesTab({ events, users = [] }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", percent_off: 15, amount_off_cents: 1000, max_redemptions: "", expires_at: "", event_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/promo-codes"); setCodes(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not load promo codes"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || form.code.length < 2) { toast.error("Enter a code (2+ characters)"); return; }
    const body = { code: form.code.trim().toUpperCase() };
    if (form.type === "percent") body.percent_off = Number(form.percent_off);
    else body.amount_off_cents = Math.round(Number(form.amount_off_cents));
    if (form.max_redemptions) body.max_redemptions = Number(form.max_redemptions);
    if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
    if (form.event_id) body.event_id = form.event_id;
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", body);
      toast.success("Promo code created");
      setOpen(false);
      setForm({ code: "", type: "percent", percent_off: 15, amount_off_cents: 1000, max_redemptions: "", expires_at: "", event_id: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  const toggle = async (id, currentlyActive) => {
    try {
      await api.patch(`/admin/promo-codes/${id}/toggle?active=${!currentlyActive}`);
      toast.success(currentlyActive ? "Deactivated" : "Activated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Toggle failed"); }
  };

  const eventTitleFor = (id) => events.find(e => e.event_id === id)?.title || id;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-eyebrow">Promo codes</div>
          <div className="text-sm text-neutral-600 mt-1">Managed by Stripe. Customers see an "Add promo code" field on the checkout page.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>setBulkOpen(true)} className="rounded-full" data-testid="bulk-gift-btn"><Gift size={14} className="mr-1" /> Bulk gift</Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={()=>setOpen(true)} className="rounded-full bg-black text-white btn-pill" data-testid="new-promo-btn"><Plus size={14} className="mr-1" /> New code</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">New promo code</DialogTitle>
              <DialogDescription className="text-sm text-neutral-500">Codes are stored on Stripe and applied at checkout.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="text-eyebrow mb-1">Code</div>
                <Input placeholder="SUMMER25" value={form.code} onChange={e=>setForm({...form, code: e.target.value.toUpperCase()})} className="rounded-xl uppercase" data-testid="promo-code" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Discount type</div>
                <div className="flex gap-2">
                  <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${form.type==="percent" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                    <input type="radio" name="promotype" value="percent" checked={form.type==="percent"} onChange={()=>setForm({...form, type:"percent"})} className="hidden" data-testid="promo-type-percent" />
                    % off
                  </label>
                  <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${form.type==="fixed" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                    <input type="radio" name="promotype" value="fixed" checked={form.type==="fixed"} onChange={()=>setForm({...form, type:"fixed"})} className="hidden" data-testid="promo-type-fixed" />
                    $ off
                  </label>
                </div>
              </div>
              {form.type === "percent" ? (
                <div>
                  <div className="text-eyebrow mb-1">Percent off</div>
                  <Input type="number" min="1" max="100" value={form.percent_off} onChange={e=>setForm({...form, percent_off: e.target.value})} className="rounded-xl" data-testid="promo-percent" />
                </div>
              ) : (
                <div>
                  <div className="text-eyebrow mb-1">Amount off (cents)</div>
                  <Input type="number" min="1" value={form.amount_off_cents} onChange={e=>setForm({...form, amount_off_cents: e.target.value})} className="rounded-xl" data-testid="promo-amount" />
                  <div className="text-[11px] text-neutral-500 mt-1">Enter cents. Example: 1000 = $10.00 off.</div>
                </div>
              )}
              <div>
                <div className="text-eyebrow mb-1">Max redemptions <span className="text-neutral-400 text-xs">(optional)</span></div>
                <Input type="number" min="1" placeholder="Unlimited" value={form.max_redemptions} onChange={e=>setForm({...form, max_redemptions: e.target.value})} className="rounded-xl" data-testid="promo-max" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Expires <span className="text-neutral-400 text-xs">(optional)</span></div>
                <Input type="datetime-local" value={form.expires_at} onChange={e=>setForm({...form, expires_at: e.target.value})} className="rounded-xl" data-testid="promo-expires" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Restrict to event <span className="text-neutral-400 text-xs">(optional)</span></div>
                <select value={form.event_id} onChange={e=>setForm({...form, event_id: e.target.value})} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="promo-event">
                  <option value="">Any event</option>
                  {events.map(ev => <option key={ev.event_id} value={ev.event_id}>{ev.title}</option>)}
                </select>
                {form.event_id && <div className="text-[11px] text-neutral-500 mt-1">Note: event restriction is recorded as metadata; Stripe applies the code to any event. Enforce with a future webhook check if needed.</div>}
              </div>
              <Button onClick={create} disabled={busy} className="w-full rounded-full bg-black text-white btn-pill" data-testid="promo-save-btn">{busy ? "Creating…" : "Create promo code"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <BulkGiftDialog open={bulkOpen} onOpenChange={setBulkOpen} users={users} onDone={load} />

      <div className="card-elegant overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Redemptions</TableHead><TableHead>Expires</TableHead><TableHead>Event</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-8">Loading…</TableCell></TableRow>}
            {!loading && codes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-8">No promo codes yet.</TableCell></TableRow>}
            {codes.map(c => (
              <TableRow key={c.id} data-testid={`promo-row-${c.id}`} className={c.active ? "" : "opacity-60"}>
                <TableCell className="font-mono text-sm">{c.code}</TableCell>
                <TableCell>{c.percent_off != null ? `${c.percent_off}% off` : c.amount_off_cents != null ? `$${(c.amount_off_cents/100).toFixed(2)} off` : "—"}</TableCell>
                <TableCell>{c.times_redeemed} / {c.max_redemptions ?? "∞"}</TableCell>
                <TableCell>{c.expires_at ? new Date(c.expires_at * 1000).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-xs text-neutral-600">{c.event_id ? eventTitleFor(c.event_id) : "Any"}</TableCell>
                <TableCell>{c.active ? <span className="text-emerald-700 font-medium">Active</span> : <span className="text-neutral-500">Inactive</span>}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={()=>toggle(c.id, c.active)} data-testid={`promo-toggle-${c.id}`}>{c.active ? "Deactivate" : "Activate"}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}


function SalesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [status, setStatus] = useState("paid");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/sales/by-event?range=${range}&status=${status}`);
      setRows(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load sales");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [range, status]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get(`/admin/sales/export.csv?range=${range}&status=${status}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales_${range}_${status}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  const totalRevenue = rows.reduce((s, r) => s + (r.revenue_cents || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + (r.tickets_paid || 0), 0);
  const totalRefunded = rows.reduce((s, r) => s + (r.tickets_refunded || 0), 0);
  const totalCapacity = rows.reduce((s, r) => s + (r.capacity || 0), 0);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-eyebrow">Sales by event</div>
          <div className="text-sm text-neutral-600 mt-1">Filter by date range or ticket status. Click an event row to jump to its attendees.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-neutral-100 rounded-full p-1" data-testid="sales-range-filter">
            {["7d","30d","all"].map(k => (
              <button key={k} onClick={()=>setRange(k)} data-testid={`sales-range-${k}`} className={`px-3 py-1 rounded-full text-xs ${range===k ? "bg-black text-white" : "text-neutral-600 hover:text-black"}`}>
                {k === "7d" ? "Last 7 days" : k === "30d" ? "Last 30 days" : "All time"}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-neutral-100 rounded-full p-1" data-testid="sales-status-filter">
            {[["paid","Paid only"],["all","Include refunds"]].map(([k, label]) => (
              <button key={k} onClick={()=>setStatus(k)} data-testid={`sales-status-${k}`} className={`px-3 py-1 rounded-full text-xs ${status===k ? "bg-black text-white" : "text-neutral-600 hover:text-black"}`}>{label}</button>
            ))}
          </div>
          <Button variant="outline" onClick={exportCsv} className="rounded-full" data-testid="sales-export-csv"><Download size={14} className="mr-2" /> Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="sales-totals">
        <Metric label="Revenue" value={`$${(totalRevenue/100).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 2})}`} data-testid="sales-total-revenue" />
        <Metric label="Tickets paid" value={totalPaid} data-testid="sales-total-paid" />
        <Metric label="Refunds" value={totalRefunded} data-testid="sales-total-refunds" />
        <Metric label="Overall fill" value={totalCapacity ? `${Math.round(totalPaid/totalCapacity*100)}%` : "—"} data-testid="sales-total-fill" />
      </div>

      <div className="card-elegant overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Date</TableHead><TableHead>Sold</TableHead><TableHead>Refunded</TableHead><TableHead>Fill</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">No events match this filter.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.event_id} data-testid={`sales-row-${r.event_id}`}>
                <TableCell>
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{r.venue}{r.category ? ` · ${r.category}` : ""}{r.is_published === false ? " · Draft" : ""}</div>
                </TableCell>
                <TableCell className="text-sm">{r.date ? formatEventDate(r.date, { month: "short", day: "numeric", year: "numeric" }) : "—"}</TableCell>
                <TableCell>{r.tickets_paid} / {r.capacity}</TableCell>
                <TableCell>{r.tickets_refunded || 0}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, r.fill_rate || 0)}%` }} />
                    </div>
                    <span className="text-xs text-neutral-600 tabular-nums">{r.fill_rate || 0}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">${((r.revenue_cents||0)/100).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}


function BulkGiftDialog({ open, onOpenChange, users, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [type, setType] = useState("percent");
  const [amount, setAmount] = useState(25);
  const [note, setNote] = useState("");
  const [expiresDays, setExpiresDays] = useState(30);
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const reset = () => { setSelected(new Set()); setSearch(""); setType("percent"); setAmount(25); setNote(""); setExpiresDays(30); setMaxRedemptions(1); setResults(null); };

  const active = users.filter(u => !u.suspended && !u.deleted_at);
  const filtered = active.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const toggle = (id) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const send = async () => {
    if (selected.size === 0) return toast.error("Pick at least one recipient");
    if (!amount || amount < 1) return toast.error("Enter a discount amount");
    setBusy(true);
    try {
      const payload = {
        user_ids: Array.from(selected),
        discount_type: type,
        amount: Number(amount),
        personal_note: note || null,
        expires_in_days: Number(expiresDays) || null,
        max_redemptions: Number(maxRedemptions) || 1,
      };
      const { data } = await api.post("/admin/promos/bulk-gift", payload);
      setResults(data);
      toast.success(`${data.sent} code${data.sent === 1 ? "" : "s"} sent${data.failed ? ` · ${data.failed} failed` : ""}`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk gift failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><Gift size={18} /> Bulk gift promo codes</DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            Pick a group of users — each gets a unique Stripe promotion code emailed to them. Codes are stored on Stripe and apply at checkout.
          </DialogDescription>
        </DialogHeader>

        {results ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="font-medium">Done — {results.sent} sent{results.failed ? ` · ${results.failed} failed` : ""}</div>
              <div className="text-xs opacity-80 mt-1">{results.discount} · code emailed to each person</div>
            </div>
            <div className="rounded-xl border border-neutral-200 max-h-72 overflow-y-auto" data-testid="bulk-gift-results">
              {results.results.map(r => (
                <div key={r.user_id} className={`flex items-center justify-between px-4 py-2 border-b last:border-b-0 text-sm ${r.ok ? "" : "bg-rose-50"}`}>
                  <div className="font-mono text-xs">{r.email}</div>
                  {r.ok ? <span className="font-mono text-xs text-emerald-700">{r.code}</span> : <span className="text-xs text-rose-700" title={r.error}>failed</span>}
                </div>
              ))}
            </div>
            <Button onClick={()=>{ reset(); onOpenChange(false); }} className="w-full rounded-full bg-black text-white btn-pill" data-testid="bulk-gift-close">Done</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-eyebrow mb-1">Discount type</div>
                <div className="flex gap-2">
                  <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${type==="percent" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                    <input type="radio" checked={type==="percent"} onChange={()=>setType("percent")} className="hidden" data-testid="bulk-gift-type-percent" />% off
                  </label>
                  <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${type==="fixed" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                    <input type="radio" checked={type==="fixed"} onChange={()=>setType("fixed")} className="hidden" data-testid="bulk-gift-type-fixed" />$ off
                  </label>
                </div>
              </div>
              <div>
                <div className="text-eyebrow mb-1">{type === "percent" ? "Percent off (1-100)" : "Amount off (cents)"}</div>
                <Input type="number" min="1" value={amount} onChange={e=>setAmount(e.target.value)} className="rounded-xl" data-testid="bulk-gift-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-eyebrow mb-1">Expires in (days)</div>
                <Input type="number" min="1" value={expiresDays} onChange={e=>setExpiresDays(e.target.value)} className="rounded-xl" data-testid="bulk-gift-expires" />
              </div>
              <div>
                <div className="text-eyebrow mb-1">Max uses per code</div>
                <Input type="number" min="1" value={maxRedemptions} onChange={e=>setMaxRedemptions(e.target.value)} className="rounded-xl" data-testid="bulk-gift-max" />
              </div>
            </div>
            <div>
              <div className="text-eyebrow mb-1">Personal note (shown in email, optional)</div>
              <Textarea rows={2} value={note} onChange={e=>setNote(e.target.value)} placeholder="On the house for last night — see you at the next one." className="rounded-xl" data-testid="bulk-gift-note" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-eyebrow">Recipients ({selected.size} selected)</div>
                <div className="flex gap-2 text-xs">
                  <button onClick={()=>setSelected(new Set(filtered.map(u => u.user_id)))} className="underline underline-offset-2 hover:text-black" data-testid="bulk-gift-select-filtered">Select {filtered.length}</button>
                  <button onClick={()=>setSelected(new Set())} className="underline underline-offset-2 hover:text-black" data-testid="bulk-gift-clear">Clear</button>
                </div>
              </div>
              <Input placeholder="Search by name or email…" value={search} onChange={e=>setSearch(e.target.value)} className="rounded-xl mb-2" data-testid="bulk-gift-search" />
              <div className="rounded-xl border border-neutral-200 max-h-64 overflow-y-auto" data-testid="bulk-gift-user-list">
                {filtered.length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-500">No users match.</div>
                ) : filtered.map(u => (
                  <label key={u.user_id} className="flex items-center gap-3 px-4 py-2 border-b last:border-b-0 cursor-pointer hover:bg-cream/40" data-testid={`bulk-gift-user-${u.user_id}`}>
                    <input type="checkbox" checked={selected.has(u.user_id)} onChange={()=>toggle(u.user_id)} className="h-4 w-4 accent-black" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{u.name || "—"}</div>
                      <div className="text-xs text-neutral-500 font-mono truncate">{u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={send} disabled={busy || selected.size === 0} className="w-full rounded-full bg-black text-white btn-pill" data-testid="bulk-gift-send">
              {busy ? "Sending…" : `Create & email ${selected.size} code${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmailLogTab() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (kindFilter) params.set("kind", kindFilter);
      if (search.trim()) params.set("q", search.trim());
      params.set("limit", "200");
      const [l, s] = await Promise.all([
        api.get(`/admin/email/log?${params.toString()}`),
        api.get("/admin/email/log/stats?hours=168"),  // 7 day stats
      ]);
      setRows(l.data);
      setStats(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load email log"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter, kindFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

  const KIND_LABELS = {
    ticket_confirmation: "Ticket confirmation",
    admin_broadcast: "Admin broadcast",
    promo_gift: "Promo gift",
    reminder: "24h reminder",
    password_reset: "Password reset",
    mutual_match: "Mutual match",
    new_message: "New message",
    vip_welcome: "VIP welcome",
    welcome: "Welcome",
    generic: "Other",
  };
  const STATUS_STYLES = {
    sent: "bg-emerald-100 text-emerald-800",
    error: "bg-rose-100 text-rose-800",
    skipped: "bg-neutral-200 text-neutral-700",
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-eyebrow">Email log</div>
        <h2 className="font-heading text-2xl mt-1">Every email that left the system — and whether it made it.</h2>
        <p className="text-sm text-neutral-600 mt-2">Sent from <span className="font-mono">hello@offlineandavailable.com</span>. If a send fails (bounced email, Resend rate limit, unverified recipient on test domains), the exact error shows here instead of silently disappearing.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-6" data-testid="email-log-stats">
          <StatPill label="Sent" value={stats.sent} tone="bg-emerald-50 text-emerald-900" />
          <StatPill label="Errors" value={stats.error} tone="bg-rose-50 text-rose-900" />
          <StatPill label="Skipped" value={stats.skipped} tone="bg-neutral-100 text-neutral-800" />
          <StatPill label="Total · 7d" value={stats.total} tone="bg-cream text-neutral-800" />
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="rounded-xl border border-neutral-200 bg-white text-sm px-3 py-2" data-testid="email-log-status">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="error">Errors</option>
          <option value="skipped">Skipped</option>
        </select>
        <select value={kindFilter} onChange={e=>setKindFilter(e.target.value)} className="rounded-xl border border-neutral-200 bg-white text-sm px-3 py-2" data-testid="email-log-kind">
          <option value="">All kinds</option>
          {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <form onSubmit={e=>{ e.preventDefault(); load(); }} className="flex gap-2 flex-1 max-w-md">
          <Input placeholder="Search by email or subject…" value={search} onChange={e=>setSearch(e.target.value)} className="rounded-xl" data-testid="email-log-search" />
          <Button type="submit" variant="outline" className="rounded-full" data-testid="email-log-search-btn">Search</Button>
        </form>
        <Button variant="outline" onClick={load} className="rounded-full ml-auto" data-testid="email-log-refresh"><RotateCcw size={14} className="mr-2" /> Refresh</Button>
      </div>

      <div className="card-elegant overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Status</TableHead><TableHead>Kind</TableHead><TableHead>Recipient</TableHead><TableHead>Subject</TableHead><TableHead>When</TableHead><TableHead>Details</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">Loading…</TableCell></TableRow>
             : rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">No emails match these filters yet.</TableCell></TableRow>
             : rows.map(r => (
              <TableRow key={r.log_id} data-testid={`email-log-row-${r.log_id}`}>
                <TableCell><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${STATUS_STYLES[r.status] || "bg-neutral-200 text-neutral-700"}`}>{r.status}</span></TableCell>
                <TableCell className="text-xs">{KIND_LABELS[r.kind] || r.kind}</TableCell>
                <TableCell className="font-mono text-xs">{r.to}</TableCell>
                <TableCell className="text-xs max-w-xs truncate" title={r.subject}>{r.subject}</TableCell>
                <TableCell className="text-xs text-neutral-500">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</TableCell>
                <TableCell>
                  {r.error ? (
                    <Button size="sm" variant="outline" onClick={()=>setPreview(r)} className="text-rose-700 border-rose-200 hover:bg-rose-50 rounded-full h-7 px-3" data-testid={`email-log-error-${r.log_id}`}>View error</Button>
                  ) : r.resend_id ? (
                    <span className="font-mono text-[10px] text-neutral-400">{r.resend_id.slice(0, 10)}…</span>
                  ) : <span className="text-neutral-300 text-xs">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!preview} onOpenChange={(v)=>{ if (!v) setPreview(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Email error</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">What Resend returned when this send was attempted.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div><span className="text-eyebrow">To</span><div className="font-mono">{preview.to}</div></div>
              <div><span className="text-eyebrow">Subject</span><div>{preview.subject}</div></div>
              <div><span className="text-eyebrow">When</span><div className="text-neutral-600">{new Date(preview.created_at).toLocaleString()}</div></div>
              <div><span className="text-eyebrow">Error from Resend</span><pre className="whitespace-pre-wrap text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3 mt-1">{preview.error}</pre></div>
              {preview.context && Object.keys(preview.context).length > 0 && (
                <div><span className="text-eyebrow">Context</span><pre className="whitespace-pre-wrap text-xs bg-neutral-50 border border-neutral-200 rounded-xl p-3 mt-1">{JSON.stringify(preview.context, null, 2)}</pre></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatPill = ({ label, value, tone }) => (
  <div className={`rounded-2xl px-4 py-3 ${tone}`}>
    <div className="text-eyebrow opacity-80">{label}</div>
    <div className="font-heading text-3xl tabular-nums mt-1">{value}</div>
  </div>
);

